import { useApp } from '@/context/AppContext'
import { useTheme } from '@/context/ThemeContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order for support questions
const SUPPORT_ORDER = ['Strongly support', 'Support', 'Neutral / Unsure', 'Oppose', 'Strongly oppose']

// Sort responses
const sortResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = SUPPORT_ORDER.indexOf(a.label)
    const bIdx = SUPPORT_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

export function HowToNavigate() {
  const { state } = useApp()
  const { theme, toggleTheme, flashIndicator } = useTheme()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  // Get break definition
  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Check if region break is selected (to show province hint)
  const isRegionBreak = selectedBreak === 'region'

  // Get example question data
  const exampleQuestion = data?.questions['Support_NewParks']

  // Build rows based on current break
  const rows = breakValues.map(value => {
    const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
    let questionData = exampleQuestion?.data[filterKey]
    if (!questionData && selectedProvince) {
      questionData = exampleQuestion?.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
    }
    return { value, filterKey, data: questionData }
  }).filter(row => row.data)

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
        How to Navigate the Dashboard
      </h1>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        {/* Callout box with arrows - RIGHT aligned under the breaks dropdown */}
        <div className="flex justify-end mb-6">
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
            <span className="text-red-500 text-2xl animate-bounce">&#8593;</span>
            <span className="text-slate-700 dark:text-slate-300">
              Use Select Break to Segment Data
            </span>
            <span className="text-red-500 text-2xl animate-bounce">&#8593;</span>
          </div>
        </div>

        {/* Province hint when Region break is selected */}
        {isRegionBreak && (
          <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <span className="text-blue-500 text-2xl">&#8594;</span>
            <span className="text-slate-700 dark:text-slate-300">
              Select a Province above to narrow down the regions shown
            </span>
            <span className="text-blue-500 text-2xl">&#8594;</span>
          </div>
        )}

        <div className="space-y-4 text-slate-600 dark:text-slate-300">
          <p>
            Every Dashboard page features one of the questions from the survey. Click on the
            breaks on the right to break down the results based on demographics or groupings.
          </p>

          <p>
            Select a break and/or a Province segment to further drill down into sub groups of
            survey respondents. The Regions break will present all regions across Canada until
            you select a Province to narrow those results.
          </p>

          <p>
            You can also switch between light and dark mode using the toggle in the header:{' '}
            <button
              onClick={() => { toggleTheme(); flashIndicator(); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 transition-colors"
              title="Toggle dark mode"
            >
              {theme === 'dark' ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              <span className="text-xs">{theme === 'dark' ? 'Light' : 'Dark'}</span>
            </button>
          </p>

          <p>
            Below is an example of one question from the survey for you to familiarize yourself
            with the functionality of the dashboard. You will notice if you hover your cursor
            over cells you will see additional details and sometimes contextual explanations.
          </p>
        </div>
      </div>

      {/* Example Question */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          Example Question
        </h2>
        <p className="text-slate-600 dark:text-slate-300 italic mb-6">
          "Do you support or oppose creating new parks and protected areas in Canada?"
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-700">
          {SUPPORT_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('support', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {rows.length > 0 ? (
            rows.map(row => (
              <div key={row.filterKey} className="flex items-center gap-2">
                <div className="w-32 text-sm text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                  {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                </div>
                <div className="flex-1">
                  <HorizontalStackedBar
                    segments={sortResponses(row.data!.responses)}
                    scaleType="support"
                    height={isBreakActive ? 24 : 28}
                    showLabels={false}
                    normalize={true}
                  />
                </div>
              </div>
            ))
          ) : (
            <p className="text-slate-500 dark:text-slate-400 italic">
              Loading example data...
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
